import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, Mail, Clock, Play, Pause, Edit2, Plus, Trash2, 
  Check, X, Eye, Sparkles, Settings, ChevronDown, ChevronUp,
  ShoppingCart, Gift, Star, Users, Calendar, RefreshCw,
  ArrowRight, GripVertical, AlertCircle, UserPlus, Heart, Bell, Tag, BookOpen, Copy, FileEdit, Save,
  Wand2, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import RichTextEditor from '../RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for trigger types
const TRIGGER_ICONS = {
  welcome: UserPlus,
  abandoned_cart: ShoppingCart,
  purchase_thank_you: Gift,
  review_request: Star,
  win_back: RefreshCw,
  birthday: Calendar,
  browse_abandonment: Eye,
  loyalty_milestone: Heart,
  back_in_stock: Bell
};

// Trigger type descriptions
const TRIGGER_DESCRIPTIONS = {
  welcome: "Automatically sent when a new subscriber signs up",
  abandoned_cart: "Recover lost sales by reminding customers about their cart",
  purchase_thank_you: "Build relationships with a thank you after purchase",
  review_request: "Request feedback 7 days after purchase",
  win_back: "Re-engage subscribers who haven't interacted in 30+ days",
  birthday: "Celebrate subscribers with a special birthday offer",
  browse_abandonment: "Follow up when users browse products but don't add to cart",
  loyalty_milestone: "Reward customers when they reach purchase milestones",
  back_in_stock: "Notify subscribers when out-of-stock items return"
};

export const EmailAutomations = ({ headers }) => {
  const [triggers, setTriggers] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('triggers');
  const [showSequenceDialog, setShowSequenceDialog] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [expandedFlow, setExpandedFlow] = useState(null);
  // New state for custom trigger types
  const [showCreateTriggerDialog, setShowCreateTriggerDialog] = useState(false);
  const [predefinedEvents, setPredefinedEvents] = useState([]);
  const [customTriggerForm, setCustomTriggerForm] = useState({
    trigger_type: '',
    name: '',
    description: '',
    trigger_event: '',
    delay_minutes: 0
  });
  // Template Library state
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [templateLibrary, setTemplateLibrary] = useState([]);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState('');
  
  // Template Editor state
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [newTemplateForm, setNewTemplateForm] = useState({
    name: '',
    category: 'welcome',
    description: '',
    subject: '',
    preview_text: '',
    content_html: '',
    cta_text: 'Learn More',
    cta_url: '/',
    tags: []
  });

  useEffect(() => {
    fetchData();
    fetchPredefinedEvents();
    fetchTemplateLibrary();
  }, []);

  const fetchPredefinedEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/emails/automations/trigger-events`, { headers });
      setPredefinedEvents(res.data);
    } catch (error) {
      console.error('Failed to fetch trigger events');
    }
  };

  const fetchTemplateLibrary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/emails/automations/templates/library`, { headers });
      setTemplateLibrary(res.data.templates || []);
      setTemplateCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch template library');
    }
  };

  const fetchData = async () => {
    try {
      const [triggersRes, sequencesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/emails/automations/triggers`, { headers }),
        axios.get(`${API_URL}/api/emails/automations/sequences`, { headers }),
        axios.get(`${API_URL}/api/emails/automations/stats`, { headers })
      ]);
      setTriggers(triggersRes.data);
      setSequences(sequencesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch automations');
    } finally {
      setLoading(false);
    }
  };

  const toggleTrigger = async (triggerId) => {
    try {
      await axios.post(`${API_URL}/api/emails/automations/triggers/${triggerId}/toggle`, {}, { headers });
      toast.success('Trigger updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle trigger');
    }
  };

  const openEditDialog = (trigger) => {
    setEditingTrigger({
      ...trigger,
      content_html: trigger.content_html || '',
      subject: trigger.subject || '',
      delay_minutes: trigger.delay_minutes || 0,
      cta_text: trigger.cta_text || 'Shop Now',
      cta_url: trigger.cta_url || '/products'
    });
    setShowEditDialog(true);
  };

  const saveTrigger = async () => {
    try {
      await axios.put(`${API_URL}/api/emails/automations/triggers/${editingTrigger.id}`, {
        subject: editingTrigger.subject,
        content_html: editingTrigger.content_html,
        delay_minutes: parseInt(editingTrigger.delay_minutes) || 0,
        is_active: editingTrigger.is_active,
        cta_text: editingTrigger.cta_text,
        cta_url: editingTrigger.cta_url
      }, { headers });
      toast.success('Trigger email saved');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save trigger');
    }
  };

  const generateAIContent = async () => {
    if (!editingTrigger) return;
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/email`, {
        campaign_type: editingTrigger.trigger_type || 'trigger',
        subject_line: editingTrigger.subject,
        tone: 'friendly',
        include_cta: true,
        custom_prompt: aiPrompt || `Generate a ${editingTrigger.name || editingTrigger.trigger_type} email for HomeLyph freeze dryers. Make it warm, engaging, and action-oriented.`
      }, { headers });
      
      if (res.data.success && res.data.content) {
        const content = res.data.content;
        setEditingTrigger(prev => ({
          ...prev,
          subject: content.subject_lines?.[0] || prev.subject,
          content_html: buildEmailHtml(content),
          cta_text: content.cta_text || prev.cta_text
        }));
        toast.success('Content generated!');
      }
      setShowAiDialog(false);
      setAiPrompt('');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setAiGenerating(false);
    }
  };

  const buildEmailHtml = (content) => {
    return `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h1 style="color: #003456; margin-bottom: 20px;">${content.headline || ''}</h1>
  ${content.body_html || ''}
  ${content.cta_text ? `
  <p style="margin-top: 30px;">
    <a href="{{cta_url}}" style="background: #003456; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">{{cta_text}}</a>
  </p>` : ''}
  ${content.ps_line ? `<p style="margin-top: 30px; font-style: italic; color: #666;">P.S. ${content.ps_line}</p>` : ''}
</div>`;
  };

  // Sequence Management
  const openSequenceDialog = (sequence = null) => {
    if (sequence) {
      setEditingSequence({
        ...sequence,
        emails: sequence.emails || []
      });
    } else {
      setEditingSequence({
        name: '',
        description: '',
        trigger_type: 'signup',
        emails: [
          { delay_days: 0, subject: 'Welcome to HomeLyph!', content_html: '' }
        ]
      });
    }
    setShowSequenceDialog(true);
  };

  const addEmailToSequence = () => {
    const lastEmail = editingSequence.emails[editingSequence.emails.length - 1];
    const newDelay = (lastEmail?.delay_days || 0) + 3;
    setEditingSequence(prev => ({
      ...prev,
      emails: [...prev.emails, { delay_days: newDelay, subject: '', content_html: '' }]
    }));
  };

  const removeEmailFromSequence = (index) => {
    setEditingSequence(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const updateSequenceEmail = (index, field, value) => {
    setEditingSequence(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => 
        i === index ? { ...email, [field]: value } : email
      )
    }));
  };

  const saveSequence = async () => {
    try {
      if (editingSequence.id) {
        await axios.put(`${API_URL}/api/emails/automations/sequences/${editingSequence.id}`, editingSequence, { headers });
        toast.success('Sequence updated');
      } else {
        await axios.post(`${API_URL}/api/emails/automations/sequences`, editingSequence, { headers });
        toast.success('Sequence created');
      }
      setShowSequenceDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save sequence');
    }
  };

  const deleteSequence = async (sequenceId) => {
    if (!window.confirm('Are you sure you want to delete this sequence?')) return;
    try {
      await axios.delete(`${API_URL}/api/emails/automations/sequences/${sequenceId}`, { headers });
      toast.success('Sequence deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sequence');
    }
  };

  const toggleSequence = async (sequenceId, currentState) => {
    try {
      await axios.put(`${API_URL}/api/emails/automations/sequences/${sequenceId}`, {
        is_active: !currentState
      }, { headers });
      toast.success(`Sequence ${!currentState ? 'activated' : 'paused'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle sequence');
    }
  };

  // Custom Trigger Type Management
  const createCustomTriggerType = async () => {
    if (!customTriggerForm.trigger_type || !customTriggerForm.name) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/emails/automations/trigger-types`, customTriggerForm, { headers });
      toast.success('Custom trigger type created!');
      setShowCreateTriggerDialog(false);
      setCustomTriggerForm({
        trigger_type: '',
        name: '',
        description: '',
        trigger_event: '',
        delay_minutes: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create trigger type');
    }
  };

  const deleteCustomTrigger = async (triggerType) => {
    if (!window.confirm(`Delete custom trigger "${triggerType}"? This will also delete its email template.`)) return;
    try {
      await axios.delete(`${API_URL}/api/emails/automations/trigger-types/${triggerType}`, { headers });
      toast.success('Custom trigger deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete trigger');
    }
  };

  // Template Library Functions
  const applyTemplate = (template) => {
    if (editingTrigger) {
      // Apply to trigger email editor
      setEditingTrigger(prev => ({
        ...prev,
        subject: template.subject,
        content_html: template.content_html,
        cta_text: template.cta_text,
        cta_url: template.cta_url
      }));
      setShowTemplateLibrary(false);
      toast.success('Template applied!');
    } else if (editingSequence) {
      // Add to sequence as new email
      const newEmail = {
        delay_days: editingSequence.emails.length > 0 ? 3 : 0,
        subject: template.subject,
        content_html: template.content_html
      };
      setEditingSequence(prev => ({
        ...prev,
        emails: [...prev.emails, newEmail]
      }));
      setShowTemplateLibrary(false);
      toast.success('Template added to sequence!');
    }
  };

  const filteredTemplates = selectedTemplateCategory 
    ? templateLibrary.filter(t => t.category === selectedTemplateCategory)
    : templateLibrary;

  // Template Editor Functions
  const openTemplateEditor = (template = null) => {
    if (template) {
      setEditingTemplate({...template});
    } else {
      setEditingTemplate(null);
      setNewTemplateForm({
        name: '',
        category: 'welcome',
        description: '',
        subject: '',
        preview_text: '',
        content_html: '',
        cta_text: 'Learn More',
        cta_url: '/',
        tags: []
      });
    }
    setShowTemplateEditor(true);
  };

  const saveTemplate = async () => {
    setTemplateSaving(true);
    try {
      if (editingTemplate) {
        // Update existing template
        await axios.put(`${API_URL}/api/emails/automations/templates/library/${editingTemplate.id}`, editingTemplate, { headers });
        toast.success('Template updated!');
      } else {
        // Create new template
        if (!newTemplateForm.name || !newTemplateForm.subject || !newTemplateForm.content_html) {
          toast.error('Please fill in name, subject, and content');
          setTemplateSaving(false);
          return;
        }
        await axios.post(`${API_URL}/api/emails/automations/templates/library`, newTemplateForm, { headers });
        toast.success('Template created!');
      }
      setShowTemplateEditor(false);
      setEditingTemplate(null);
      fetchTemplateLibrary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/emails/automations/templates/library/${templateId}`, { headers });
      toast.success('Template deleted!');
      fetchTemplateLibrary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const duplicateTemplate = async (templateId) => {
    try {
      const res = await axios.post(`${API_URL}/api/emails/automations/templates/library/${templateId}/duplicate`, {}, { headers });
      toast.success('Template duplicated!');
      fetchTemplateLibrary();
      // Open the new template for editing
      if (res.data.template) {
        setEditingTemplate(res.data.template);
        setShowTemplateEditor(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  // AI Template Generation
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please describe what kind of email you want to create');
      return;
    }
    
    setAiGenerating(true);
    
    try {
      // Map template categories to campaign types
      const categoryToCampaign = {
        welcome: 'welcome_series',
        abandoned_cart: 'abandoned_cart',
        promotional: 'promotional',
        win_back: 'win_back',
        review_request: 'post_purchase',
        newsletter: 'newsletter',
        transactional: 'transactional',
        other: 'promotional'
      };
      
      const category = editingTemplate ? editingTemplate.category : newTemplateForm.category;
      
      const response = await axios.post(`${API_URL}/api/ai/email`, {
        campaign_type: categoryToCampaign[category] || 'promotional',
        subject_line: editingTemplate?.subject || newTemplateForm.subject || '',
        product_focus: '',
        tone: 'friendly',
        include_cta: true,
        target_audience: '',
        custom_prompt: aiPrompt
      }, { headers });
      
      if (response.data.success && response.data.content) {
        const content = response.data.content;
        
        // Update the template with generated content
        const updates = {
          subject: content.subject_lines?.[0] || '',
          preview_text: content.preview_text || '',
          content_html: content.body_html || '',
          cta_text: content.cta_text || 'Learn More',
          cta_url: content.cta_url_suggestion || '/'
        };
        
        if (editingTemplate) {
          setEditingTemplate({
            ...editingTemplate,
            ...updates
          });
        } else {
          setNewTemplateForm({
            ...newTemplateForm,
            ...updates,
            name: newTemplateForm.name || `AI Generated - ${category}`,
            description: aiPrompt.substring(0, 100)
          });
        }
        
        toast.success('Email content generated!');
        setAiPrompt('');
      } else {
        throw new Error('Failed to generate content');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.message || 'Failed to generate content');
    } finally {
      setAiGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-32 bg-gray-100 rounded-xl"></div>
        <div className="animate-pulse h-64 bg-gray-100 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-automations">
      {/* Stats Overview */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Triggers</p>
                <p className="text-2xl font-bold">{stats.triggers?.active_triggers || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Emails Sent</p>
                <p className="text-2xl font-bold">{stats.triggers?.emails_sent || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold">{stats.triggers?.emails_pending || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Sequences</p>
                <p className="text-2xl font-bold">{stats.sequences?.active_sequences || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for Triggers vs Sequences vs Templates */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="triggers" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Zap className="h-4 w-4 mr-2" /> Trigger Emails
          </TabsTrigger>
          <TabsTrigger value="sequences" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Mail className="h-4 w-4 mr-2" /> Email Sequences
          </TabsTrigger>
          <TabsTrigger value="templates" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="templates-tab">
            <FileEdit className="h-4 w-4 mr-2" /> Templates
          </TabsTrigger>
        </TabsList>

        {/* Trigger Emails Tab */}
        <TabsContent value="triggers" className="mt-6">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Trigger Emails</h3>
                <p className="text-sm text-gray-500">Automated emails sent when specific actions occur</p>
              </div>
              <Button onClick={() => setShowCreateTriggerDialog(true)} variant="outline" className="border-[#003456] text-[#003456]" data-testid="create-trigger-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Custom Trigger
              </Button>
            </div>
            <div className="divide-y">
              {triggers.map((trigger) => {
                const IconComponent = TRIGGER_ICONS[trigger.trigger_type] || (trigger.is_custom ? Tag : Mail);
                const description = TRIGGER_DESCRIPTIONS[trigger.trigger_type] || trigger.description;
                return (
                  <div key={trigger.id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`trigger-${trigger.trigger_type}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`p-3 rounded-xl ${trigger.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <IconComponent className={`h-6 w-6 ${trigger.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{trigger.name}</h4>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${trigger.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {trigger.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {trigger.is_custom && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">Custom</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            {trigger.delay_minutes > 0 && (
                              <span className="flex items-center gap-1 text-amber-600">
                                <Clock className="h-3 w-3" />
                                Delay: {trigger.delay_minutes < 60 ? `${trigger.delay_minutes} min` : 
                                  trigger.delay_minutes < 1440 ? `${Math.round(trigger.delay_minutes / 60)} hr` :
                                  `${Math.round(trigger.delay_minutes / 1440)} days`}
                              </span>
                            )}
                            {trigger.stats && (
                              <>
                                <span>Sent: {trigger.stats.sent || 0}</span>
                                <span>Opened: {trigger.stats.opened || 0}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowPreview(trigger)} data-testid={`preview-${trigger.trigger_type}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(trigger)} data-testid={`edit-${trigger.trigger_type}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => toggleTrigger(trigger.id)}
                          className={trigger.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}
                          data-testid={`toggle-${trigger.trigger_type}`}
                        >
                          {trigger.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        {trigger.is_custom && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => deleteCustomTrigger(trigger.trigger_type)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-${trigger.trigger_type}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {triggers.length === 0 && (
                <div className="p-12 text-center">
                  <Zap className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No trigger emails configured</p>
                  <p className="text-sm text-gray-400 mt-1">Trigger emails will appear here when created</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Email Sequences Tab - Visual Flow Builder */}
        <TabsContent value="sequences" className="mt-6">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Email Sequences</h3>
                <p className="text-sm text-gray-500">Multi-step automated email campaigns triggered by user actions</p>
              </div>
              <Button onClick={() => openSequenceDialog()} className="bg-[#003456] hover:bg-[#001d30]" data-testid="create-sequence-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Sequence
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {sequences.map((sequence) => (
                <div key={sequence.id} className="border rounded-xl overflow-hidden" data-testid={`sequence-${sequence.id}`}>
                  {/* Sequence Header */}
                  <div 
                    className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setExpandedFlow(expandedFlow === sequence.id ? null : sequence.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${sequence.is_active ? 'bg-green-100' : 'bg-gray-200'}`}>
                          <Mail className={`h-5 w-5 ${sequence.is_active ? 'text-green-600' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{sequence.name}</h4>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${sequence.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                              {sequence.is_active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{sequence.description || `${sequence.emails?.length || 0} emails in sequence`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 mr-4">
                          {sequence.active_subscribers || 0} active subscribers
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); toggleSequence(sequence.id, sequence.is_active); }}
                          className={sequence.is_active ? 'text-red-600' : 'text-green-600'}
                        >
                          {sequence.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openSequenceDialog(sequence); }}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); deleteSequence(sequence.id); }} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        {expandedFlow === sequence.id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                      </div>
                    </div>
                  </div>
                  
                  {/* Visual Flow - Expanded */}
                  {expandedFlow === sequence.id && (
                    <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
                      {/* Trigger Start */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shadow-lg">
                          <Zap className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Trigger: {sequence.trigger_type}</p>
                          <p className="text-sm text-gray-500">Sequence starts when this event occurs</p>
                        </div>
                      </div>
                      
                      {/* Flow Line & Emails */}
                      <div className="ml-6 border-l-2 border-dashed border-gray-300 pl-8 space-y-6">
                        {sequence.emails?.map((email, idx) => (
                          <div key={idx} className="relative">
                            {/* Connector dot */}
                            <div className="absolute -left-[41px] top-4 w-4 h-4 rounded-full bg-[#003456] border-4 border-white shadow"></div>
                            
                            {/* Email Card */}
                            <div className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs font-semibold text-[#003456] bg-blue-50 px-2 py-1 rounded">
                                      Email {idx + 1}
                                    </span>
                                    {email.delay_days > 0 && (
                                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        Wait {email.delay_days} {email.delay_days === 1 ? 'day' : 'days'}
                                      </span>
                                    )}
                                    {email.delay_days === 0 && idx === 0 && (
                                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                        Immediate
                                      </span>
                                    )}
                                  </div>
                                  <h5 className="font-medium text-gray-900">{email.subject || 'No subject'}</h5>
                                  {email.content_html && (
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2" 
                                       dangerouslySetInnerHTML={{ __html: email.content_html.substring(0, 100) + '...' }} />
                                  )}
                                </div>
                                <Mail className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* End Marker */}
                        <div className="relative">
                          <div className="absolute -left-[41px] top-2 w-4 h-4 rounded-full bg-gray-400 border-4 border-white shadow"></div>
                          <div className="bg-gray-100 rounded-lg p-3 inline-block">
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-600" />
                              Sequence Complete
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {sequences.length === 0 && (
                <div className="p-12 text-center border-2 border-dashed rounded-xl">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 font-medium">No email sequences yet</p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">Create your first automated email sequence</p>
                  <Button onClick={() => openSequenceDialog()} className="bg-[#003456] hover:bg-[#001d30]">
                    <Plus className="h-4 w-4 mr-2" /> Create Sequence
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Templates Tab - Email Template Editor */}
        <TabsContent value="templates" className="mt-6">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Email Templates</h3>
                <p className="text-sm text-gray-500">Create and edit reusable email templates for campaigns and triggers</p>
              </div>
              <Button onClick={() => openTemplateEditor()} className="bg-[#003456] hover:bg-[#001d30]" data-testid="create-template-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Template
              </Button>
            </div>

            {/* Category Filter */}
            <div className="p-4 border-b bg-gray-25 flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Filter:</span>
              <button
                onClick={() => setSelectedTemplateCategory('')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  !selectedTemplateCategory ? 'bg-[#003456] text-white border-[#003456]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#003456]'
                }`}
              >
                All ({templateLibrary.length})
              </button>
              {templateCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedTemplateCategory(cat)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors capitalize ${
                    selectedTemplateCategory === cat ? 'bg-[#003456] text-white border-[#003456]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#003456]'
                  }`}
                >
                  {cat.replace(/_/g, ' ')} ({templateLibrary.filter(t => t.category === cat).length})
                </button>
              ))}
            </div>

            {/* Templates Grid */}
            <div className="p-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map(template => (
                  <div 
                    key={template.id} 
                    className="border rounded-xl overflow-hidden hover:shadow-md transition-shadow group"
                    data-testid={`template-card-${template.id}`}
                  >
                    {/* Template Header */}
                    <div className="bg-gray-50 p-3 border-b">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{template.name}</h4>
                          <p className="text-xs text-gray-500 truncate">{template.description}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 capitalize whitespace-nowrap">
                            {template.category?.replace(/_/g, ' ')}
                          </span>
                          {template.is_default && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Template Preview */}
                    <div className="p-3">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Subject:</strong> {template.subject}
                      </p>
                      <div className="bg-gray-50 border rounded-lg p-2 max-h-24 overflow-hidden text-xs">
                        <div 
                          dangerouslySetInnerHTML={{ __html: template.content_html?.substring(0, 200) + '...' }}
                          className="opacity-70"
                        />
                      </div>
                      
                      {/* Tags */}
                      {template.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Template Actions */}
                    <div className="p-3 border-t bg-gray-25 flex items-center justify-between">
                      <div className="text-xs text-gray-400">
                        {template.updated_at ? `Updated ${new Date(template.updated_at).toLocaleDateString()}` : ''}
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => duplicateTemplate(template.id)}
                          title="Duplicate"
                          data-testid={`duplicate-template-${template.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openTemplateEditor(template)}
                          title="Edit"
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {!template.is_default && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteTemplate(template.id)}
                            className="text-red-600 hover:bg-red-50"
                            title="Delete"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="p-12 text-center border-2 border-dashed rounded-xl">
                  <FileEdit className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 font-medium">No templates in this category</p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">Create a new template to get started</p>
                  <Button onClick={() => openTemplateEditor()} className="bg-[#003456] hover:bg-[#001d30]">
                    <Plus className="h-4 w-4 mr-2" /> Create Template
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Trigger Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Trigger Email: {editingTrigger?.name}
            </DialogTitle>
          </DialogHeader>
          {editingTrigger && (
            <div className="grid md:grid-cols-2 gap-6 pt-4">
              {/* Editor Column */}
              <div className="space-y-4">
                {/* AI Generator */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      <span className="font-medium text-purple-800">AI Content Generator</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => setShowTemplateLibrary(true)}
                        variant="outline"
                        className="border-blue-300 text-blue-700"
                        size="sm"
                        data-testid="template-library-btn"
                      >
                        <BookOpen className="h-4 w-4 mr-1" /> Templates
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setShowAiDialog(true)}
                        variant="outline"
                        className="border-purple-300 text-purple-700"
                        size="sm"
                        data-testid="ai-custom-prompt-btn"
                      >
                        Custom Prompt
                      </Button>
                      <Button
                        type="button"
                        onClick={() => { setAiPrompt(''); generateAIContent(); }}
                        disabled={aiGenerating}
                        className="bg-purple-600 hover:bg-purple-700"
                        size="sm"
                        data-testid="ai-quick-generate-btn"
                      >
                        {aiGenerating ? 'Generating...' : 'Quick Generate'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                  <Input
                    value={editingTrigger.subject}
                    onChange={(e) => setEditingTrigger({ ...editingTrigger, subject: e.target.value })}
                    placeholder="Email subject line"
                    data-testid="trigger-subject-input"
                  />
                </div>

                {/* Delay */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delay (minutes)</label>
                  <Input
                    type="number"
                    value={editingTrigger.delay_minutes}
                    onChange={(e) => setEditingTrigger({ ...editingTrigger, delay_minutes: e.target.value })}
                    min="0"
                    data-testid="trigger-delay-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">How long to wait after the trigger event before sending</p>
                </div>

                {/* CTA */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                    <Input
                      value={editingTrigger.cta_text}
                      onChange={(e) => setEditingTrigger({ ...editingTrigger, cta_text: e.target.value })}
                      placeholder="Shop Now"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTA URL</label>
                    <Input
                      value={editingTrigger.cta_url}
                      onChange={(e) => setEditingTrigger({ ...editingTrigger, cta_url: e.target.value })}
                      placeholder="/products"
                    />
                  </div>
                </div>

                {/* Content Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Content (HTML)</label>
                  <textarea
                    value={editingTrigger.content_html}
                    onChange={(e) => setEditingTrigger({ ...editingTrigger, content_html: e.target.value })}
                    className="w-full h-48 p-3 border rounded-lg font-mono text-sm"
                    placeholder="<div>Your email HTML content...</div>"
                    data-testid="trigger-content-textarea"
                  />
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="trigger-active"
                    checked={editingTrigger.is_active}
                    onChange={(e) => setEditingTrigger({ ...editingTrigger, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="trigger-active" className="text-sm font-medium">Active (emails will be sent)</label>
                </div>
              </div>

              {/* Live Preview Column */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Live Preview</label>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Updates in real-time</span>
                </div>
                <div className="border rounded-xl overflow-hidden shadow-sm">
                  {/* Email Header Preview */}
                  <div className="bg-gray-100 p-3 border-b">
                    <p className="text-xs text-gray-500">Subject</p>
                    <p className="text-sm font-medium text-gray-900">{editingTrigger.subject || 'No subject'}</p>
                  </div>
                  {/* Email Body Preview */}
                  <div className="bg-white p-4 max-h-[400px] overflow-y-auto">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: editingTrigger.content_html
                          .replace(/\{\{cta_text\}\}/g, editingTrigger.cta_text || 'Shop Now')
                          .replace(/\{\{cta_url\}\}/g, editingTrigger.cta_url || '/products')
                      }} 
                    />
                    {!editingTrigger.content_html && (
                      <div className="text-center py-8 text-gray-400">
                        <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>Start typing to see preview</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button onClick={saveTrigger} className="bg-[#003456]" data-testid="save-trigger-btn">Save Trigger Email</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview: {showPreview?.name}</DialogTitle>
          </DialogHeader>
          {showPreview && (
            <div className="pt-4">
              <div className="bg-gray-100 p-3 rounded-t-lg border">
                <p className="text-sm"><strong>Subject:</strong> {showPreview.subject}</p>
              </div>
              <div className="border border-t-0 rounded-b-lg p-4 bg-white max-h-96 overflow-y-auto">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: showPreview.content_html
                      ?.replace(/\{\{cta_text\}\}/g, showPreview.cta_text || 'Shop Now')
                      .replace(/\{\{cta_url\}\}/g, showPreview.cta_url || '/products') || '<p class="text-gray-400">No content configured</p>'
                  }} 
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Custom Prompt Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI Content Generator
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-700">
                <strong>Tips:</strong> Be specific about tone, target audience, promotions, or any special offers to include.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Custom Context/Instructions</label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Example: Create a warm welcome email for new subscribers. Mention our free recipe eBook and include a 10% discount code WELCOME10."
                className="w-full mt-1 p-3 border rounded-lg h-32 resize-none"
                data-testid="ai-prompt-textarea"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAiDialog(false)}>Cancel</Button>
              <Button
                onClick={generateAIContent}
                disabled={aiGenerating}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="ai-generate-btn"
              >
                {aiGenerating ? 'Generating...' : 'Generate Content'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sequence Editor Dialog */}
      <Dialog open={showSequenceDialog} onOpenChange={setShowSequenceDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {editingSequence?.id ? 'Edit Sequence' : 'Create Email Sequence'}
            </DialogTitle>
          </DialogHeader>
          {editingSequence && (
            <div className="space-y-6 pt-4">
              {/* Sequence Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Name</label>
                  <Input
                    value={editingSequence.name}
                    onChange={(e) => setEditingSequence({ ...editingSequence, name: e.target.value })}
                    placeholder="e.g., Welcome Series"
                    data-testid="sequence-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type</label>
                  <select
                    value={editingSequence.trigger_type}
                    onChange={(e) => setEditingSequence({ ...editingSequence, trigger_type: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    data-testid="sequence-trigger-select"
                  >
                    <option value="signup">New Signup</option>
                    <option value="purchase">Purchase Complete</option>
                    <option value="abandoned_cart">Cart Abandoned</option>
                    <option value="inactive">Subscriber Inactive (30 days)</option>
                    <option value="birthday">Birthday</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <Input
                  value={editingSequence.description || ''}
                  onChange={(e) => setEditingSequence({ ...editingSequence, description: e.target.value })}
                  placeholder="Brief description of this sequence"
                />
              </div>

              {/* Email Steps */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Email Steps</label>
                  <Button variant="outline" size="sm" onClick={addEmailToSequence} data-testid="add-email-step-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Email
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {editingSequence.emails.map((email, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="bg-[#003456] text-white text-xs font-bold px-2 py-1 rounded">
                            Step {idx + 1}
                          </span>
                          {idx === 0 ? (
                            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Immediate</span>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-amber-600" />
                              <Input
                                type="number"
                                value={email.delay_days}
                                onChange={(e) => updateSequenceEmail(idx, 'delay_days', parseInt(e.target.value) || 0)}
                                className="w-16 h-7 text-xs"
                                min="0"
                              />
                              <span className="text-xs text-gray-500">days after previous</span>
                            </div>
                          )}
                        </div>
                        {idx > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeEmailFromSequence(idx)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="space-y-3">
                        <Input
                          value={email.subject}
                          onChange={(e) => updateSequenceEmail(idx, 'subject', e.target.value)}
                          placeholder="Email subject line"
                          className="bg-white"
                        />
                        <textarea
                          value={email.content_html}
                          onChange={(e) => updateSequenceEmail(idx, 'content_html', e.target.value)}
                          placeholder="Email content (HTML)"
                          className="w-full p-2 border rounded-lg text-sm h-24 bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowSequenceDialog(false)}>Cancel</Button>
                <Button onClick={saveSequence} className="bg-[#003456]" data-testid="save-sequence-btn">
                  {editingSequence.id ? 'Update Sequence' : 'Create Sequence'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Custom Trigger Type Dialog */}
      <Dialog open={showCreateTriggerDialog} onOpenChange={setShowCreateTriggerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-purple-600" />
              Create Custom Trigger Type
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-700">
                <strong>Custom triggers</strong> let you define new automated email workflows beyond the defaults.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type ID *</label>
              <Input
                value={customTriggerForm.trigger_type}
                onChange={(e) => setCustomTriggerForm({
                  ...customTriggerForm,
                  trigger_type: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                })}
                placeholder="e.g., vip_anniversary"
                data-testid="custom-trigger-type-input"
              />
              <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, underscores only)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
              <Input
                value={customTriggerForm.name}
                onChange={(e) => setCustomTriggerForm({ ...customTriggerForm, name: e.target.value })}
                placeholder="e.g., VIP Anniversary"
                data-testid="custom-trigger-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={customTriggerForm.description}
                onChange={(e) => setCustomTriggerForm({ ...customTriggerForm, description: e.target.value })}
                placeholder="e.g., Sent on customer's VIP anniversary date"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Event</label>
              <select
                value={customTriggerForm.trigger_event}
                onChange={(e) => setCustomTriggerForm({ ...customTriggerForm, trigger_event: e.target.value })}
                className="w-full p-2 border rounded-lg"
                data-testid="custom-trigger-event-select"
              >
                <option value="">Select an event...</option>
                {predefinedEvents.map(event => (
                  <option key={event.event} value={event.event}>
                    {event.label} - {event.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Delay (minutes)</label>
              <Input
                type="number"
                value={customTriggerForm.delay_minutes}
                onChange={(e) => setCustomTriggerForm({ ...customTriggerForm, delay_minutes: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">How long to wait after the event before sending</p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateTriggerDialog(false)}>Cancel</Button>
              <Button onClick={createCustomTriggerType} className="bg-purple-600 hover:bg-purple-700" data-testid="create-custom-trigger-btn">
                Create Trigger Type
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Library Dialog */}
      <Dialog open={showTemplateLibrary} onOpenChange={setShowTemplateLibrary}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Email Template Library
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Quick Start:</strong> Choose a professionally designed template to jumpstart your email. You can customize it after applying.
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Filter by:</span>
              <button
                onClick={() => setSelectedTemplateCategory('')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  !selectedTemplateCategory ? 'bg-[#003456] text-white border-[#003456]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#003456]'
                }`}
              >
                All
              </button>
              {templateCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedTemplateCategory(cat)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors capitalize ${
                    selectedTemplateCategory === cat ? 'bg-[#003456] text-white border-[#003456]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#003456]'
                  }`}
                >
                  {cat.replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* Template Grid */}
            <div className="grid md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto">
              {filteredTemplates.map(template => (
                <div key={template.id} className="border rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                  <div className="bg-gray-50 p-3 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{template.name}</h4>
                        <p className="text-xs text-gray-500">{template.description}</p>
                      </div>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 capitalize">
                        {template.category.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Subject:</strong> {template.subject}
                    </p>
                    <div className="bg-white border rounded-lg p-2 max-h-32 overflow-hidden text-xs">
                      <div 
                        dangerouslySetInnerHTML={{ __html: template.content_html.substring(0, 300) + '...' }}
                        className="opacity-60"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex flex-wrap gap-1">
                        {template.tags?.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => applyTemplate(template)}
                        className="bg-[#003456]"
                        data-testid={`use-template-${template.id}`}
                      >
                        <Copy className="h-3 w-3 mr-1" /> Use Template
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No templates in this category</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog */}
      <Dialog open={showTemplateEditor} onOpenChange={setShowTemplateEditor}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5" />
              {editingTemplate ? `Edit Template: ${editingTemplate.name}` : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          
          {/* AI Generation Section */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="h-5 w-5 text-purple-600" />
              <h4 className="font-medium text-purple-900">AI Template Generator</h4>
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Powered by AI</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Describe the email you want (e.g., 'A promotional email for our summer sale with 20% off all freeze dryers')"
                className="flex-1 bg-white"
                disabled={aiGenerating}
                data-testid="ai-prompt-input"
              />
              <Button
                onClick={generateWithAI}
                disabled={aiGenerating || !aiPrompt.trim()}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="ai-generate-btn"
              >
                {aiGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Generate</>
                )}
              </Button>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              AI will generate subject line, preview text, and email content based on your description and selected category.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-4">
            {/* Editor Column */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <Input
                    value={editingTemplate ? editingTemplate.name : newTemplateForm.name}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, name: e.target.value})
                      : setNewTemplateForm({...newTemplateForm, name: e.target.value})
                    }
                    placeholder="e.g., Welcome Email V2"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={editingTemplate ? editingTemplate.category : newTemplateForm.category}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, category: e.target.value})
                      : setNewTemplateForm({...newTemplateForm, category: e.target.value})
                    }
                    className="w-full p-2 border rounded-lg text-sm"
                    data-testid="template-category-select"
                  >
                    <option value="welcome">Welcome</option>
                    <option value="abandoned_cart">Abandoned Cart</option>
                    <option value="promotional">Promotional</option>
                    <option value="win_back">Win Back</option>
                    <option value="review_request">Review Request</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="transactional">Transactional</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Input
                  value={editingTemplate ? editingTemplate.description : newTemplateForm.description}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, description: e.target.value})
                    : setNewTemplateForm({...newTemplateForm, description: e.target.value})
                  }
                  placeholder="Brief description of this template"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
                <Input
                  value={editingTemplate ? editingTemplate.subject : newTemplateForm.subject}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, subject: e.target.value})
                    : setNewTemplateForm({...newTemplateForm, subject: e.target.value})
                  }
                  placeholder="Email subject line"
                  data-testid="template-subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preview Text</label>
                <Input
                  value={editingTemplate ? editingTemplate.preview_text : newTemplateForm.preview_text}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, preview_text: e.target.value})
                    : setNewTemplateForm({...newTemplateForm, preview_text: e.target.value})
                  }
                  placeholder="Text shown in email preview"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                  <Input
                    value={editingTemplate ? editingTemplate.cta_text : newTemplateForm.cta_text}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, cta_text: e.target.value})
                      : setNewTemplateForm({...newTemplateForm, cta_text: e.target.value})
                    }
                    placeholder="Shop Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA URL</label>
                  <Input
                    value={editingTemplate ? editingTemplate.cta_url : newTemplateForm.cta_url}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, cta_url: e.target.value})
                      : setNewTemplateForm({...newTemplateForm, cta_url: e.target.value})
                    }
                    placeholder="/products"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Content (HTML) *</label>
                <textarea
                  value={editingTemplate ? editingTemplate.content_html : newTemplateForm.content_html}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, content_html: e.target.value})
                    : setNewTemplateForm({...newTemplateForm, content_html: e.target.value})
                  }
                  className="w-full h-56 p-3 border rounded-lg font-mono text-sm"
                  placeholder="<div>Your email HTML content...</div>"
                  data-testid="template-content-textarea"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use {'{{cta_text}}'} and {'{{cta_url}}'} as placeholders for the button
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <Input
                  value={editingTemplate 
                    ? (editingTemplate.tags || []).join(', ')
                    : (newTemplateForm.tags || []).join(', ')
                  }
                  onChange={(e) => {
                    const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                    editingTemplate 
                      ? setEditingTemplate({...editingTemplate, tags})
                      : setNewTemplateForm({...newTemplateForm, tags});
                  }}
                  placeholder="welcome, series, introduction"
                />
              </div>
            </div>

            {/* Live Preview Column */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Live Preview</label>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Updates in real-time</span>
              </div>
              <div className="border rounded-xl overflow-hidden shadow-sm sticky top-4">
                {/* Email Header Preview */}
                <div className="bg-gray-100 p-3 border-b">
                  <p className="text-xs text-gray-500">Subject</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(editingTemplate ? editingTemplate.subject : newTemplateForm.subject) || 'No subject'}
                  </p>
                </div>
                {/* Email Body Preview */}
                <div className="bg-white p-4 max-h-[500px] overflow-y-auto">
                  {(editingTemplate?.content_html || newTemplateForm.content_html) ? (
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: (editingTemplate ? editingTemplate.content_html : newTemplateForm.content_html)
                          .replace(/\{\{cta_text\}\}/g, (editingTemplate ? editingTemplate.cta_text : newTemplateForm.cta_text) || 'Learn More')
                          .replace(/\{\{cta_url\}\}/g, (editingTemplate ? editingTemplate.cta_url : newTemplateForm.cta_url) || '/')
                      }} 
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Start typing to see preview</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowTemplateEditor(false)}>Cancel</Button>
              <Button 
                onClick={saveTemplate} 
                className="bg-[#003456]" 
                disabled={templateSaving}
                data-testid="save-template-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {templateSaving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailAutomations;
